import { isBigNumber, isConstantNode, isOperatorNode, isSymbolNode, parse, } from "mathjs";
import { evaluateElectricalValue, evaluateMechanicalValue, } from "./EvaluateExpression";
import { electricalEvaluationMathjs, mechanicalEvaluationMathjs } from "./Evaluators";
export function isExpression(expression) {
    return expression.trim().startsWith("=");
}
// Value nodes contain simple values or negation of the same.
function isValueNode(node) {
    if (isConstantNode(node))
        return true;
    if (isOperatorNode(node)) {
        if (node.fn == "unaryMinus")
            return true;
        return false;
    }
    return false;
}
// Detect implicit operations as defined by mathjs - for example "1mm" is an implicit multiplication
// of '1' and 'mm'.
// Note that parenthesis is an operator node!
function isImplicitLeaf(node) {
    if (!node.implicit)
        return false;
    if (node.args?.length === 2) {
        const maybeNum = node.args[0];
        const maybeUnit = node.args[1];
        return isValueNode(maybeNum) && isSymbolNode(maybeUnit) && node.fn === "multiply";
    }
    else if (node.args?.length === 1) {
        return isValueNode(node.args[0]);
    }
    return false;
}
/**
 * Wraps the mathjs parser in a result type so that callers can safely call parse without having to handle
 * exceptions.
 */
function safeParse(input) {
    try {
        const rootNode = parse(input);
        return { success: true, rootNode };
    }
    catch (_err) {
        return { success: false, unparsedInput: input };
    }
}
/**
 * Check if a string represents a "plain value".  What we mean by that is that the string is something
 * like "1mm" or "3.0".  Anything that contains non-trivial math or e.g. function calls is not a plain value.
 * This is needed in cases where we want to parse values to perform unit conversion but we do NOT want
 * to perform any kind of mathematical evaluation of those values.
 *
 * This translates to: we only allow symbols, constants, and implicit operations
 */
export function isPlainValue(expression) {
    const parserResult = safeParse(expression);
    if (parserResult.success) {
        const { rootNode } = parserResult;
        return (rootNode.filter((node) => !isValueNode(node) && !isSymbolNode(node) && (!isOperatorNode(node) || !isImplicitLeaf(node))).length === 0);
    }
    else {
        return false;
    }
}
export function normalizeEvaluatedExpression(evaluation, addUnitPostfix = false, returnBigNumberType = true, convertToUnit, defaultUnit) {
    if (typeof evaluation === "string") {
        return evaluation;
    }
    else if (typeof evaluation === "number") {
        return evaluation;
    }
    else if (typeof evaluation === "boolean") {
        return evaluation;
    }
    else if (isBigNumber(evaluation)) {
        if (returnBigNumberType) {
            return evaluation;
        }
        else if (defaultUnit && convertToUnit) {
            // The evaluation is unitless, we need to convert it to the default unit before returning it.
            // We need to use the mechanical evaluation here because only `m` or `rad` conversions are supported.
            return mechanicalEvaluationMathjs.unit(evaluation, defaultUnit).toNumber(convertToUnit);
        }
        return electricalEvaluationMathjs.number(evaluation);
    }
    if (evaluation === undefined) {
        return undefined;
    }
    if (convertToUnit) {
        return evaluation.toNumber(convertToUnit);
    }
    else if (addUnitPostfix) {
        return evaluation.toString();
    }
    else if (typeof evaluation.value === "number") {
        return evaluation.value;
    }
    return JSON.parse(`${evaluation.value}`);
}
export function stringifyPropertyValue(propertyData, context) {
    if (typeof propertyData.value === "string") {
        return stringifyElectricalString(propertyData.value, propertyData.unit, context);
    }
    else if (typeof propertyData.value === "number") {
        return stringifyNumber(propertyData.value, propertyData.unit);
    }
    else if (typeof propertyData.value === "boolean") {
        return stringifyBoolean(propertyData.value, propertyData.unit);
    }
    return "";
}
function stringifyNumber(propertyValue, propertyUnit) {
    if (propertyUnit && !propertyValue.toString().endsWith(propertyUnit)) {
        return `${humanizeBigNumber(propertyValue.toString())}${propertyUnit}`;
    }
    else {
        return humanizeBigNumber(propertyValue.toString());
    }
}
function stringifyBoolean(propertyValue, propertyUnit) {
    if (propertyUnit && !propertyValue.toString().endsWith(propertyUnit)) {
        return `${propertyValue.toString()} ${propertyUnit}`;
    }
    else {
        return propertyValue.toString();
    }
}
function resolveSimpleUnitValues(propertyUnit, propertyValue) {
    if (propertyUnit && !propertyValue.endsWith(propertyUnit)) {
        return `${propertyValue}${propertyUnit}`;
    }
    else {
        return propertyValue;
    }
}
export function stringifyElectricalString(propertyValue, propertyUnit, context) {
    if (!isExpression(propertyValue)) {
        return resolveSimpleUnitValues(propertyUnit, propertyValue);
    }
    const evaluatedExpression = evaluateElectricalValue(propertyValue, undefined, context);
    const normalizedEvaluatedExpression = normalizeEvaluatedExpression(evaluatedExpression, !propertyUnit);
    return humanizeEvaluatedExpression(normalizedEvaluatedExpression, propertyValue, propertyUnit);
}
function humanizeEvaluatedExpression(normalizedEvaluatedExpression, propertyValue, propertyUnit) {
    if (typeof normalizedEvaluatedExpression === "string") {
        const newValue = humanizeBigNumber(normalizedEvaluatedExpression);
        if (normalizedEvaluatedExpression === propertyValue) {
            if (propertyUnit && !normalizedEvaluatedExpression.endsWith(propertyUnit)) {
                return `${normalizedEvaluatedExpression}${propertyUnit}`;
            }
            else {
                return normalizedEvaluatedExpression;
            }
        }
        else if (propertyUnit && !newValue.endsWith(propertyUnit)) {
            return `${newValue}${propertyUnit}`;
        }
        else {
            return newValue;
        }
    }
    else if (typeof normalizedEvaluatedExpression === "number") {
        // Dynamically select precision for toFixed formatting to address edge case where a value
        // of zero would have otherwise formatted to 0.00000...
        const precision = normalizedEvaluatedExpression === 0 ? 1 : 20;
        const fixedValue = normalizedEvaluatedExpression.toFixed(precision);
        const newValue = humanizeBigNumber(fixedValue);
        if (normalizedEvaluatedExpression === Number(propertyValue)) {
            if (propertyUnit && !newValue.endsWith(propertyUnit)) {
                return `${normalizedEvaluatedExpression}${propertyUnit}`;
            }
            else {
                return fixedValue;
            }
        }
        else if (propertyUnit && !newValue.endsWith(propertyUnit)) {
            return `${newValue}${propertyUnit}`;
        }
        else {
            return newValue;
        }
    }
    else if (typeof normalizedEvaluatedExpression === "boolean") {
        return normalizedEvaluatedExpression.toString();
    }
    else if (electricalEvaluationMathjs.typeOf(normalizedEvaluatedExpression) === "BigNumber") {
        const newValue = humanizeBigNumber(electricalEvaluationMathjs.number(normalizedEvaluatedExpression).toString());
        if (propertyUnit && !newValue.endsWith(propertyUnit)) {
            return `${newValue}${propertyUnit}`;
        }
        else {
            return newValue;
        }
    }
    return propertyValue;
}
export function stringifyMechanicalString(value, context) {
    const evaluatedExpression = evaluateMechanicalValue(value, undefined, context);
    const normalizedEvaluatedExpression = normalizeEvaluatedExpression(evaluatedExpression, true);
    return humanizeEvaluatedExpression(normalizedEvaluatedExpression, value, undefined);
}
function humanizeNumbersUnderOne(valueUnit) {
    const units = ["m", "u", "n", "p", "f", "a", "z"];
    let i = 0;
    let iteratorValue = valueUnit.clone().toString();
    do {
        iteratorValue = electricalEvaluationMathjs.format(electricalEvaluationMathjs.multiply(electricalEvaluationMathjs.bignumber(iteratorValue), electricalEvaluationMathjs.bignumber(1000)), { notation: "fixed" });
        i++;
    } while (iteratorValue.startsWith("0") && i < units.length);
    const convertedValue = electricalEvaluationMathjs.format(valueUnit.toNumber(units[i - 1]), { notation: "fixed" });
    return `${convertedValue}${units[i - 1]}` || "";
}
function humanizeNumbersOverOneThousand(valueUnit) {
    const units = ["k", "M", "G", "T", "P", "E", "Z", "Y"];
    let i = 0;
    let iteratorValue = valueUnit.clone().toString();
    do {
        iteratorValue = electricalEvaluationMathjs.format(electricalEvaluationMathjs.divide(electricalEvaluationMathjs.bignumber(iteratorValue), electricalEvaluationMathjs.bignumber(1000)), { notation: "fixed" });
        i++;
    } while (iteratorValue.toString().endsWith("0") && i < units.length);
    const convertedValue = valueUnit.toNumber(units[i - 1]);
    return `${numberWithCommas(convertedValue)}${units[i - 1]}` || "";
}
function humanizeNumbersBetweenOneAndOneThousand(value) {
    const valueParts = value.split(" ");
    if (valueParts.length === 2) {
        const numberPart = valueParts[0];
        return `${numberWithCommas(Number(numberPart))} ${valueParts[1]}` || "";
    }
    else {
        return numberWithCommas(Number(value)).toString() || "";
    }
}
function valueHasTrailingZeros(value) {
    return value.toString().endsWith("0");
}
function valueHasLeadingZeros(value) {
    return value.toString().startsWith("0");
}
export function humanizeBigNumber(value) {
    try {
        const valueUnit = electricalEvaluationMathjs.unit(value);
        const valueAsNumber = valueUnit.toNumber();
        if (valueAsNumber === 0) {
            return value;
        }
        const fixedFormatValue = electricalEvaluationMathjs.format(valueUnit, { notation: "fixed" });
        if (value && valueHasTrailingZeros(fixedFormatValue) && valueAsNumber >= 1000) {
            return humanizeNumbersOverOneThousand(valueUnit);
        }
        else if (value && valueHasLeadingZeros(fixedFormatValue) && valueAsNumber < 1) {
            return humanizeNumbersUnderOne(valueUnit);
        }
        else {
            return humanizeNumbersBetweenOneAndOneThousand(value);
        }
    }
    catch {
        return value;
    }
}
function numberWithCommas(value) {
    const parts = value.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
}
export function removeExpressionPrefix(expression) {
    return expression.trim().substring(1).trim();
}
//# sourceMappingURL=ExpressionEvaluationHelpers.js.map